import type { MessageScore, WeeklyScore } from './linkedinJourney';
export type ScoreTier = 'tier2' | 'tier1' | 'trash' | 'all';
export type ScoreMode = 'weeks' | 'messages' | 'people';
export const SCORE_METRICS = [
  ['dms_sent', 'DMs sent'],
  ['replies', 'Replies'],
  ['info_replies', 'Info replies'],
  ['overviews_sent', 'Overviews sent'],
  ['promoted_to_lead', 'Promoted to lead'],
  ['calls_booked', 'Calls booked'],
  ['calls_held', 'Calls held'],
  ['no_shows', 'No shows'],
  ['proposals_sent', 'Proposals'],
  ['deals_created', 'Deals'],
  ['deals_won', 'Won'],
] as const;
type Metric = (typeof SCORE_METRICS)[number][0];
export type ScoreRow = { key: string; label: string } & Record<Metric, number>;
function empty(key: string, label: string): ScoreRow {
  return {
    key,
    label,
    ...Object.fromEntries(SCORE_METRICS.map(([name]) => [name, 0])),
  } as ScoreRow;
}
export function manilaDay(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}
export function weekLabel(day: string) {
  return `Week of ${new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Manila' }).format(new Date(`${day}T00:00:00+08:00`)).replace('Sept', 'Sep')}`;
}
export function scoreboardRows(
  rows: readonly WeeklyScore[],
  tier: ScoreTier,
  names: ReadonlyMap<string, string> = new Map(),
) {
  const weeks = new Map<string, ScoreRow>();
  const people = new Map<string, ScoreRow>();
  for (const row of rows) {
    if (tier !== 'all' && row.tier !== tier) continue;
    if (!row.week_start) continue;
    const actor = row.actor_id ?? '';
    const week = weeks.get(row.week_start) ?? empty(row.week_start, weekLabel(row.week_start));
    const person =
      people.get(actor) ??
      empty(
        actor,
        row.actor_id === null
          ? 'Not recorded'
          : (names.get(actor) ?? `Name unavailable (${actor})`),
      );
    for (const [key] of SCORE_METRICS) {
      week[key] += row[key] ?? 0;
      person[key] += row[key] ?? 0;
    }
    weeks.set(row.week_start, week);
    people.set(actor, person);
  }
  return {
    weeks: [...weeks.values()].sort((a, b) => b.key.localeCompare(a.key)),
    people: [...people.values()].sort((a, b) => a.label.localeCompare(b.label)),
  };
}
const percent = (n: number, d: number) => (d === 0 ? '-' : `${Math.round((n / d) * 1000) / 10}%`);
export type MessageRow = {
  copy_set_id: string;
  label: string;
  people_messaged: number;
  replied: number;
  info_replied: number;
  calls_booked: number;
  replyPercent: string;
  infoPercent: string;
};
export function messageRows(rows: readonly MessageScore[], tier: ScoreTier): MessageRow[] {
  const grouped = new Map<string, MessageRow>();
  for (const row of rows) {
    if ((tier !== 'all' && row.tier !== tier) || !row.copy_set_id) continue;
    const item = grouped.get(row.copy_set_id) ?? {
      copy_set_id: row.copy_set_id,
      label: `${row.copy_set_name ?? 'Message not recorded'}${row.archived_at ? ' (archived)' : ''}`,
      people_messaged: 0,
      replied: 0,
      info_replied: 0,
      calls_booked: 0,
      replyPercent: '-',
      infoPercent: '-',
    };
    for (const key of ['people_messaged', 'replied', 'info_replied', 'calls_booked'] as const)
      item[key] += row[key] ?? 0;
    item.replyPercent = percent(item.replied, item.people_messaged);
    item.infoPercent = percent(item.info_replied, item.people_messaged);
    grouped.set(item.copy_set_id, item);
  }
  return [...grouped.values()].sort((a, b) => a.label.localeCompare(b.label));
}
function csvField(value: string | number) {
  const s =
    typeof value === 'string' &&
    /^[=+\-@\t\r]/.test(value) &&
    value !== '-' &&
    !/^[+-]?\d+(\.\d+)?$/.test(value.trim())
      ? `'${value}`
      : String(value);
  return /[",\r\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}
export function scoreboardCsv(
  mode: ScoreMode,
  weeks: readonly ScoreRow[],
  messages: readonly MessageRow[],
  people: readonly ScoreRow[],
) {
  const rows: (string | number)[][] =
    mode === 'messages'
      ? [
          [
            'Message',
            'People messaged',
            'Replies',
            'Reply %',
            'Info replies',
            'Info %',
            'Calls booked',
          ],
          ...messages.map((r) => [
            r.label,
            r.people_messaged,
            r.replied,
            r.replyPercent,
            r.info_replied,
            r.infoPercent,
            r.calls_booked,
          ]),
        ]
      : [
          [mode === 'weeks' ? 'Week' : 'Person', ...SCORE_METRICS.map(([, label]) => label)],
          ...(mode === 'weeks' ? weeks : people).map((r) => [
            r.label,
            ...SCORE_METRICS.map(([key]) => r[key]),
          ]),
        ];
  return '\ufeff' + rows.map((r) => r.map(csvField).join(',')).join('\r\n');
}
